import {
  useEmployerOrganisationOptional,
  navigateWithThreadPreservation,
  useArtifactCanvas,
  mapOpportunitySkillsForCard,
  PaneListEmptyState,
  PaneListError,
  PaneListSection,
  PaneListSkeleton,
  PaneResponsiveCardGrid,
  UnifiedListFilterBar,
  useUnifiedBrowseSearch,
  ListSortMenu,
  getPageHeader,
  getListPagination,
  managedEmployerOrganisationSwitcherEnabled,
  PageContainer,
  PageHeader,
  PageHeaderLeading,
  PageHeaderStack,
  PageHeaderActionPrimary,
  PageHeaderActions,
  useOrganisations,
  useAuth,
  ListPagination,
  captureListReturnState,
  Button,
  EMPLOYER_ORGANISATION_NEW_SETUP_PATH,
} from '@yeshuman/sdk'
import { useCallback, useEffect, useMemo } from 'react'
import { useQueries, useQueryClient } from '@tanstack/react-query'
import { useLocation, useSearchParams, useParams, useNavigate } from 'react-router-dom'
import { EmployerJobCreateFlow } from './employer-job-create-flow'
import { JobCard } from '../job-card'
import { useOpportunities, type Opportunity } from '../../hooks/use-opportunities'
import {
  useOrganisationOpportunities,
  fetchManagedOrganisationOpportunities,
  type OrganisationOpportunity,
} from '../../hooks/use-organisation-opportunities'
import { toOpportunityJobSummaryInput } from '../../utils/opportunity-job-meta'
import { Briefcase, Plus } from 'lucide-react'
import {
  EMPLOYER_JOBS_DEFAULT_OPERATORS,
  applyEmployerJobsFiltersToSearchParams,
  buildEmployerJobListFilterCriteria,
  compareEmployerJobs,
  formatEmployerJobsKeywordCommitHint,
  getEmployerJobsFilterDefinitions,
  matchesEmployerJobListFilters,
  normalizeEmployerJobsKeywordQuery,
  parseEmployerJobsFiltersFromSearchParams,
  parseEmployerJobsSort,
  SORT_FILTER_OPTIONS,
  type EmployerJobListFilters,
} from '../../lib/list-filters/employer-jobs'

export type { EmployerJobsSort } from '../../lib/list-filters/employer-jobs'

interface EmployerOpportunitiesPageProps {
  onStartConversation?: (message: string) => void
}

const EMPLOYER_JOBS_FILTER_CTX = {}
const employerJobsPagination = getListPagination('employerJobs')

export function EmployerOpportunitiesPage(_props?: EmployerOpportunitiesPageProps) {
  void _props

  const filterDefinitions = useMemo(() => getEmployerJobsFilterDefinitions(), [])

  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { slug: orgSlug } = useParams<{ slug: string }>()
  const { openCanvas, closeCanvas } = useArtifactCanvas()

  const filters = useMemo(() => {
    const parsed = parseEmployerJobsFiltersFromSearchParams(searchParams)
    return {
      ...parsed,
      page_size: employerJobsPagination.enabled ? employerJobsPagination.pageSize : parsed.page_size,
    }
  }, [searchParams])
  const filterCriteria = useMemo(() => buildEmployerJobListFilterCriteria(filters), [filters])

  const updateFilters = useCallback(
    (next: EmployerJobListFilters) => {
      setSearchParams(applyEmployerJobsFiltersToSearchParams(searchParams, next), {
        replace: true,
      })
    },
    [searchParams, setSearchParams],
  )

  const { token } = useAuth()
  const { organisations, isLoading: organisationsLoading } = useOrganisations()
  const tenantSwitcherEnabled = managedEmployerOrganisationSwitcherEnabled()
  const employerOrgCtx = useEmployerOrganisationOptional()
  const activeCtxSlug =
    tenantSwitcherEnabled && employerOrgCtx?.enabled ? employerOrgCtx.activeOrganisationSlug?.trim() ?? '' : ''

  const organisationRouteLocked = Boolean(orgSlug?.trim())

  const resolvedWorkspaceSlug = useMemo(() => {
    if (organisationRouteLocked && orgSlug?.trim()) return orgSlug.trim()
    if (tenantSwitcherEnabled && activeCtxSlug) return activeCtxSlug
    if (!tenantSwitcherEnabled && organisations.length === 1) return organisations[0].slug
    return ''
  }, [organisationRouteLocked, orgSlug, tenantSwitcherEnabled, activeCtxSlug, organisations])

  const mergeAllManaged =
    !tenantSwitcherEnabled && !resolvedWorkspaceSlug && organisations.length > 1

  const useSingleManagedList = !mergeAllManaged && resolvedWorkspaceSlug.length > 0

  const ctxOrgLoading =
    tenantSwitcherEnabled && employerOrgCtx?.enabled ? employerOrgCtx.organisationsLoading : false

  /** Managed list mode while managed-org memberships are loading (no rows yet). */
  const awaitingManagedSwitcherSlug =
    tenantSwitcherEnabled &&
    !organisationRouteLocked &&
    ctxOrgLoading &&
    organisations.length === 0

  /** Switcher expects a persisted slug — no public merged list fallback. */
  const tenantManagedWithoutSlug =
    tenantSwitcherEnabled &&
    !organisationRouteLocked &&
    !resolvedWorkspaceSlug.trim()

  const managedEmployerList =
    awaitingManagedSwitcherSlug ||
    tenantManagedWithoutSlug ||
    useSingleManagedList ||
    mergeAllManaged

  const {
    opportunities: managedRawSingle,
    pagination: managedPagination,
    isLoading: managedLoadingSingle,
    error: managedErrorSingle,
  } = useOrganisationOpportunities(useSingleManagedList ? resolvedWorkspaceSlug : '', {
    filters: employerJobsPagination.enabled ? filters : undefined,
    paginate: employerJobsPagination.enabled,
  })

  const allManagedQueries = useQueries({
    queries: organisations.map((org) => ({
      queryKey: ['organisation-opportunities', org.slug, 'full'] as const,
      queryFn: () =>
        fetchManagedOrganisationOpportunities(org.slug, token, { paginate: false }),
      enabled: !!token && mergeAllManaged,
    })),
  })

  const { rows: managedRows, managedLoading, managedError } = useMemo(() => {
    if (mergeAllManaged) {
      const loading = allManagedQueries.some((q) => q.isLoading)
      const err = allManagedQueries.find((q) => q.error)?.error
      const rows: { orgName: string; orgSlug: string; opp: OrganisationOpportunity }[] = []
      organisations.forEach((org, i) => {
        for (const opp of allManagedQueries[i].data?.results ?? []) {
          rows.push({ orgName: org.name, orgSlug: org.slug, opp })
        }
      })
      return { rows, managedLoading: loading, managedError: err }
    }
    if (useSingleManagedList) {
      return {
        rows: managedRawSingle.map((opp) => ({
          orgName:
            organisations.find((o) => o.slug === resolvedWorkspaceSlug)?.name ||
            resolvedWorkspaceSlug,
          orgSlug: resolvedWorkspaceSlug,
          opp,
        })),
        managedLoading: managedLoadingSingle,
        managedError: managedErrorSingle,
      }
    }
    return { rows: [], managedLoading: false, managedError: null }
  }, [
    mergeAllManaged,
    useSingleManagedList,
    organisations,
    allManagedQueries,
    managedRawSingle,
    managedLoadingSingle,
    managedErrorSingle,
    resolvedWorkspaceSlug,
  ])

  const managedFilteredRows = useMemo(
    () =>
      employerJobsPagination.enabled && useSingleManagedList
        ? managedRows
        : managedRows.filter((row) => matchesEmployerJobListFilters(row.opp, filterCriteria)),
    [managedRows, filterCriteria, useSingleManagedList],
  )

  // Public mode splits keyword ownership by operator: "contains" is served by
  // the backend q param (no client re-filter — semantics may differ); the
  // backend cannot negate, so "does not contain" keeps q client-side only.
  const publicServerQuery = filters.q_operator === 'contains' ? filters.q : ''
  const publicFilterCriteria = useMemo(
    () => ({ ...filterCriteria, q: '', keywordTerms: [] }),
    [filterCriteria],
  )
  const publicKeywordExclusion =
    filters.q_operator === 'does_not_contain' ? filterCriteria : publicFilterCriteria

  const {
    opportunities: publicResults,
    pagination,
    isLoading: publicLoading,
    error: publicError,
  } = useOpportunities(
    { q: publicServerQuery, page: filters.page, page_size: filters.page_size },
    { enabled: !managedEmployerList && !organisationsLoading },
  )

  useEffect(() => {
    return () => {
      closeCanvas()
    }
  }, [closeCanvas])

  const canOpenPostForm =
    !organisationsLoading &&
    organisations.length > 0 &&
    (!tenantSwitcherEnabled ||
      organisationRouteLocked ||
      Boolean(resolvedWorkspaceSlug.trim()))

  const needsOrganisationFirst = !organisationsLoading && organisations.length === 0
  const needsWorkspaceSelection =
    !organisationsLoading &&
    organisations.length > 0 &&
    tenantSwitcherEnabled &&
    !organisationRouteLocked &&
    !resolvedWorkspaceSlug.trim()

  const handleCreateOrganisation = (): void => {
    navigateWithThreadPreservation(navigate, EMPLOYER_ORGANISATION_NEW_SETUP_PATH, searchParams)
  }

  const opportunitiesBase: Opportunity[] = useMemo(() => {
    if (!managedEmployerList) {
      return publicResults
        .filter((o) => matchesEmployerJobListFilters(o, publicKeywordExclusion))
        .map((o) => ({
          ...o,
          organisation_slug: o.organisation_slug || resolvedWorkspaceSlug || undefined,
        }))
    }
    return managedFilteredRows.map((row) => ({
      id: row.opp.id,
      title: row.opp.title,
      description: row.opp.description,
      location: row.opp.location ?? '',
      organisation_name: row.orgName,
      organisation_slug: row.orgSlug,
      organisation_logo_url: row.opp.organisation_logo_url,
      publication_status: row.opp.publication_status,
      public_slug: row.opp.public_slug,
      published_at: row.opp.published_at,
      employment_type: row.opp.employment_type,
      work_mode: row.opp.work_mode,
      city: row.opp.city,
      country_code: row.opp.country_code,
      salary_visibility: row.opp.salary_visibility,
      salary_min: row.opp.salary_min ?? null,
      salary_max: row.opp.salary_max ?? null,
      salary_currency: row.opp.salary_currency,
      salary_period: row.opp.salary_period,
      created_at: row.opp.created_at,
      updated_at: row.opp.updated_at,
      skills: row.opp.skills.map((s) => ({
        id: s.id,
        skill_name: s.skill_name,
        requirement_type: s.requirement_type,
      })),
      experiences: row.opp.experiences.map((e) => ({
        id: e.id,
        description: e.description,
      })),
    }))
  }, [
    managedEmployerList,
    publicResults,
    managedFilteredRows,
    resolvedWorkspaceSlug,
    publicKeywordExclusion,
  ])

  const displayOpportunities = useMemo(() => {
    if (employerJobsPagination.enabled && useSingleManagedList && managedEmployerList) {
      return opportunitiesBase
    }
    const sorted = [...opportunitiesBase].sort((a, b) => compareEmployerJobs(a, b, filters.sort))
    if (employerJobsPagination.enabled && mergeAllManaged) {
      const start = (filters.page - 1) * filters.page_size
      return sorted.slice(start, start + filters.page_size)
    }
    return sorted
  }, [
    opportunitiesBase,
    filters.sort,
    filters.page,
    filters.page_size,
    useSingleManagedList,
    managedEmployerList,
    mergeAllManaged,
  ])

  const listTotal = useMemo(() => {
    if (!managedEmployerList) {
      return pagination.total
    }
    if (employerJobsPagination.enabled && useSingleManagedList) {
      return managedPagination?.total ?? 0
    }
    if (employerJobsPagination.enabled && mergeAllManaged) {
      return managedFilteredRows.length
    }
    return displayOpportunities.length
  }, [
    managedEmployerList,
    pagination.total,
    employerJobsPagination.enabled,
    useSingleManagedList,
    managedPagination?.total,
    mergeAllManaged,
    managedFilteredRows.length,
    displayOpportunities.length,
  ])

  const showListPagination =
    employerJobsPagination.enabled &&
    listTotal > filters.page_size &&
    (useSingleManagedList || mergeAllManaged || !managedEmployerList)

  const awaitingTenantOrgContext =
    tenantSwitcherEnabled &&
    !organisationRouteLocked &&
    organisations.length === 0 &&
    (organisationsLoading || ctxOrgLoading)

  const isLoading =
    organisationsLoading ||
    awaitingManagedSwitcherSlug ||
    awaitingTenantOrgContext ||
    (managedEmployerList ? managedLoading : publicLoading)
  const error = managedEmployerList ? managedError : publicError

  const setFilter = useCallback(
    <K extends keyof EmployerJobListFilters>(key: K, value: EmployerJobListFilters[K]) => {
      updateFilters({ ...filters, [key]: value, page: 1 })
    },
    [filters, updateFilters],
  )

  const { commitTextQuery, clearCommittedTextQuery } = useUnifiedBrowseSearch({
    committedQ: filters.q,
    normalizeQuery: normalizeEmployerJobsKeywordQuery,
    onUpdateQ: (q) => updateFilters({ ...filters, q, page: 1 }),
  })

  const handleCreateOpportunity = (): void => {
    if (!canOpenPostForm) return
    const lockedSlug =
      organisationRouteLocked ? orgSlug?.trim() ?? '' : tenantSwitcherEnabled ? activeCtxSlug : ''
    openCanvas({
      title: 'Create job',
      preventAutoFocus: true,
      content: (
        <EmployerJobCreateFlow
          organisations={organisations}
          lockedOrganisationSlug={lockedSlug || null}
          suggestedOrganisationSlug={
            tenantSwitcherEnabled ? activeCtxSlug || null : resolvedWorkspaceSlug || null
          }
          onClose={closeCanvas}
          onCreated={(organisationSlug, created) => {
            void queryClient.invalidateQueries({
              queryKey: ['organisation-opportunities', organisationSlug],
            })
            navigateWithThreadPreservation(
              navigate,
              `/employer/jobs/${created.id}/details`,
              searchParams,
              { state: captureListReturnState(location) },
            )
          }}
        />
      ),
    })
  }

  const openManagedJobDetail = (opportunity: Opportunity) => {
    navigateWithThreadPreservation(
      navigate,
      `/employer/jobs/${opportunity.id}/details`,
      searchParams,
      { state: captureListReturnState(location) },
    )
  }

  const getOrganisationName = (s: string): string => {
    const org = organisations.find((o) => o.slug === s)
    return org?.name || s
  }

  const pageHeader = getPageHeader(
    '/employer/jobs',
    'Jobs',
    'Create, manage, publish, and feature your job postings.'
  )
  const pageDescription = orgSlug
    ? `Manage jobs for ${getOrganisationName(orgSlug)}`
    : tenantSwitcherEnabled && activeCtxSlug
      ? `Manage jobs for ${getOrganisationName(activeCtxSlug)}`
      : pageHeader.description

  const clearAllFilters = useCallback(() => {
    updateFilters({
      ...filters,
      q: '',
      publication_status: [],
      employment_type: [],
      work_mode: [],
      ...EMPLOYER_JOBS_DEFAULT_OPERATORS,
      sort: 'updated_desc',
      page: 1,
    })
  }, [filters, updateFilters])

  const hasActiveFilters = useMemo(
    () =>
      filters.sort !== 'updated_desc' ||
      filterDefinitions.some((def) => def.isNonDefault(filters, EMPLOYER_JOBS_FILTER_CTX)),
    [filters, filterDefinitions],
  )

  const sortMenu = (
    <ListSortMenu
      options={SORT_FILTER_OPTIONS}
      value={filters.sort}
      onValueChange={(v) => setFilter('sort', parseEmployerJobsSort(v))}
      ariaLabel="Sort jobs"
    />
  )

  return (
    <PageContainer>
      <PageHeaderStack>
        <PageHeader
          leading={<PageHeaderLeading />}
          title={pageHeader.title}
          description={pageDescription}
          icon={<Briefcase className="h-5 w-5" />}
          actions={
            <PageHeaderActions
              primary={
                <PageHeaderActionPrimary
                  onClick={handleCreateOpportunity}
                  disabled={!canOpenPostForm}
                  title={
                    !canOpenPostForm
                      ? organisationsLoading
                        ? 'Loading organisations…'
                        : 'Create or link a managed organisation first'
                      : undefined
                  }
                >
                  <Plus className="h-4 w-4" aria-hidden />
                  Post New Job
                </PageHeaderActionPrimary>
              }
            />
          }
        />

        <UnifiedListFilterBar
          listId="employerJobs"
          filters={filters}
          onFiltersChange={updateFilters}
          definitions={filterDefinitions}
          ctx={EMPLOYER_JOBS_FILTER_CTX}
          hasActiveFilters={hasActiveFilters}
          onClearAll={clearAllFilters}
          toolbar={{ sortMenu }}
          search={{
            searchId: 'employer-jobs-search',
            searchPlaceholder: 'Search or add filters…',
            keywordDisplay: 'inline',
            onSearchBlur: commitTextQuery,
            committedSearchValue: filters.q,
            formatSearchCommitHint: formatEmployerJobsKeywordCommitHint,
            onClearCommittedSearch: clearCommittedTextQuery,
          }}
        />
      </PageHeaderStack>

      <PaneListSection title="Jobs" className="space-y-3" showHeading={false}>
        {isLoading ? (
          <PaneListSkeleton variant="row" count={5} ariaLabel="Loading jobs" />
        ) : error ? (
          <PaneListError message={`Failed to load jobs: ${error.message}`} />
        ) : displayOpportunities.length === 0 ? (
          <PaneListEmptyState
            icon={<Briefcase aria-hidden />}
            title={
              needsOrganisationFirst && !hasActiveFilters
                ? 'Create an organisation first'
                : 'No jobs found'
            }
            description={
              needsOrganisationFirst
                ? 'You need an organisation before you can post jobs.'
                : needsWorkspaceSelection
                  ? 'Choose a workspace from the sidebar switcher, then create a job posting.'
                  : hasActiveFilters
                    ? 'Try adjusting your filters or create a new job posting.'
                    : 'Create your first job posting to start attracting talent.'
            }
            action={
              needsOrganisationFirst ? (
                <Button type="button" onClick={handleCreateOrganisation} variant="outline">
                  <Plus className="mr-2 h-4 w-4" />
                  Create organisation
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={handleCreateOpportunity}
                  disabled={!canOpenPostForm}
                  title={
                    !canOpenPostForm
                      ? organisationsLoading
                        ? 'Loading organisations…'
                        : needsWorkspaceSelection
                          ? 'Choose a workspace from the sidebar switcher first'
                          : 'Create or link a managed organisation first'
                      : undefined
                  }
                  variant="outline"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Post Your First Job
                </Button>
              )
            }
          />
        ) : (
          <div className="space-y-2">
            <div
              className="text-xs font-normal tabular-nums text-muted-foreground"
              aria-live="polite"
              aria-atomic="true"
            >
              {listTotal} {listTotal === 1 ? 'job' : 'jobs'}
            </div>
            <PaneResponsiveCardGrid>
              {displayOpportunities.map((opportunity) => {
                const navigableManaged = managedEmployerList
                const orgLabel = opportunity.organisation_name ?? opportunity.organisation_slug ?? ''
                return (
                  <JobCard
                    key={opportunity.id}
                    className="h-full"
                    title={opportunity.title}
                    publicationStatus={opportunity.publication_status}
                    publishedAt={opportunity.published_at}
                    updatedAt={opportunity.updated_at}
                    description={opportunity.description}
                    meta={toOpportunityJobSummaryInput(opportunity)}
                    organisationLabel={orgLabel}
                    organisationLogoUrl={opportunity.organisation_logo_url}
                    skills={mapOpportunitySkillsForCard(opportunity.skills)}
                    onOpen={navigableManaged ? () => openManagedJobDetail(opportunity) : undefined}
                    footerNote={
                      !navigableManaged
                        ? 'Open jobs from employer job openings with a linked organisation to view full detail.'
                        : undefined
                    }
                  />
                )
              })}
            </PaneResponsiveCardGrid>
            {showListPagination ? (
              <ListPagination
                page={filters.page}
                pageSize={filters.page_size}
                total={listTotal}
                onPageChange={(page) => updateFilters({ ...filters, page })}
              />
            ) : null}
          </div>
        )}
      </PaneListSection>
    </PageContainer>
  )
}
